import {CSSProperties, FC, ReactNode} from 'react'
import clsx from 'clsx'
import {QRCode} from 'react-qrcode-logo'
import {
  InvoicePrintField,
  InvoicePrintLayoutSettings,
  InvoicePrintModel,
  InvoicePrintParty,
} from '../core/_models'
import {formatCell, PrintColumn} from '../core/printColumns'
import {LEGIBLE_SIGNATURE_MM} from '../core/printLayoutSettings'
import {formatNumber} from '../core/printNumbers'
import {
  identityFields,
  IdentityField,
  isProformaPrint,
  notices,
  patternLabel,
  printDocumentTitle,
  RowsTotals,
  showsPartyName,
  totalRows,
  watermarkTone,
} from '../core/printModel'
import {hasLatinLetters, persianText} from '../core/printText'
import {TAX_PUBLIC_QR_CAPTION, taxPublicQrValue} from '../core/taxPublicLink'

// The parts an A4 sheet is built from. The measuring pass renders the very same components, so
// what is measured is what is printed.

/** An identifier read left to right; tax IDs and passports in the Latin font. */
export const Code: FC<{value: string | null | undefined; className?: string}> = ({
  value,
  className,
}) => (
  <bdi dir='ltr' className={clsx('ip-code', {'ip-code--latin': hasLatinLetters(value)}, className)}>
    {value || ''}
  </bdi>
)

/** A number with Persian separators, isolated from the text around it. */
export const Num: FC<{value: string | null | undefined; className?: string}> = ({
  value,
  className,
}) => (
  <bdi dir='ltr' className={clsx('ip-num', className)}>
    {formatNumber(value)}
  </bdi>
)

const FieldValue: FC<{field: Pick<InvoicePrintField, 'value' | 'format'>}> = ({field}) => {
  switch (field.format) {
    case 'code':
      return <Code value={field.value} />
    case 'amount':
      return <Num value={field.value} />
    case 'date':
      return <bdi className='ip-date'>{field.value}</bdi>
    default:
      return <>{persianText(field.value)}</>
  }
}

const statusTone: Record<string, string> = {
  registered: 'success',
  awaitingResult: 'info',
  sending: 'info',
  notSent: 'neutral',
  cancelled: 'danger',
  superseded: 'danger',
}

export const StatusChip: FC<{invoice: InvoicePrintModel}> = ({invoice}) => (
  <span className={clsx('ip-status', `ip-status--${statusTone[invoice.status.code] || 'neutral'}`)}>
    <span className='ip-status__dot' aria-hidden='true' />
    {invoice.status.title}
    {invoice.status.isSandbox && <span className='ip-status__extra'>محیط آزمایشی</span>}
  </span>
)

const IdentityCell: FC<{field: IdentityField}> = ({field}) => (
  <div className={clsx('ip-identity__cell', {'ip-identity__cell--wide': field.wide})}>
    <div className='ip-identity__label'>{field.title}</div>
    <div className='ip-identity__value'>
      {field.format === 'code' ? (
        <Code value={field.value} />
      ) : field.format === 'date' ? (
        <bdi className='ip-date'>{field.value}</bdi>
      ) : (
        field.value
      )}
      {field.mark && <span className='ip-identity__mark'>{field.mark}</span>}
    </div>
  </div>
)

export const SheetHeaderFull: FC<{
  invoice: InvoicePrintModel
  qrValue: string
  qrId: string
}> = ({invoice, qrValue, qrId}) => {
  const taxQrValue = taxPublicQrValue(invoice)
  return (
    <header className='ip-head'>
      <div className='ip-head__top'>
        <div className='ip-head__logo'>
          {invoice.logoUrl && <img src={invoice.logoUrl} alt='' />}
        </div>
        <div className='ip-head__titles'>
          <h1 className='ip-head__title'>{printDocumentTitle(invoice)}</h1>
          <div className='ip-head__subtitle'>
            {isProformaPrint(invoice) ? (
              <>پیشنهاد قیمت؛ صورتحساب مالیاتی نیست</>
            ) : (
              <>
                الگوی {patternLabel(invoice)}
                {invoice.subject !== 1 && <> ، صورتحساب {invoice.subjectTitle}</>}
              </>
            )}
          </div>
          <StatusChip invoice={invoice} />
        </div>
        <div className='ip-head__codes'>
          {taxQrValue && (
            <div className='ip-head__qr ip-head__qr--tax'>
              {/* The organisation's link is ~220 characters: a larger code with low error correction
                  keeps its modules readable on paper. */}
              <QRCode id={`${qrId}-tax`} value={taxQrValue} size={264} quietZone={0} ecLevel='L' />
              <div className='ip-head__qr-caption'>{TAX_PUBLIC_QR_CAPTION}</div>
            </div>
          )}
          <div className='ip-head__qr'>
            <QRCode id={qrId} value={qrValue} size={200} quietZone={0} ecLevel='M' />
            <div className='ip-head__qr-caption'>نسخهٔ برخط</div>
          </div>
        </div>
      </div>
      <div className='ip-identity'>
        {identityFields(invoice).map((field) => (
          <IdentityCell key={field.key} field={field} />
        ))}
      </div>
    </header>
  )
}

export const SheetHeaderCompact: FC<{invoice: InvoicePrintModel}> = ({invoice}) => (
  <header className='ip-head-compact'>
    <span className='ip-head-compact__title'>{printDocumentTitle(invoice)} (ادامه)</span>
    {invoice.taxId && (
      <span className='ip-head-compact__item'>
        <span className='ip-head-compact__label'>شماره مالیاتی</span>
        <Code value={invoice.taxId} />
      </span>
    )}
    {invoice.invoiceNumber && (
      <span className='ip-head-compact__item'>
        <span className='ip-head-compact__label'>
          {isProformaPrint(invoice) ? 'شماره پیش‌فاکتور' : 'شماره صورتحساب'}
        </span>
        <Code value={invoice.invoiceNumber} />
      </span>
    )}
    <span className='ip-head-compact__item'>
      <span className='ip-head-compact__label'>تاریخ صدور</span>
      <bdi className='ip-date'>{invoice.issuedAt}</bdi>
    </span>
  </header>
)

export const SheetFooter: FC<{
  invoice: InvoicePrintModel
  page: number
  pages: number
  issuer: string
}> = ({invoice, page, pages, issuer}) => (
  <footer className='ip-foot'>
    <span className='ip-foot__page'>
      صفحهٔ {page} از {pages}
    </span>
    <span className='ip-foot__id'>
      {invoice.taxId ? (
        <>
          شماره منحصر به فرد مالیاتی <Code value={invoice.taxId} />
        </>
      ) : (
        <>
          کد پیگیری سامانه <Code value={invoice.trackingCode} />
        </>
      )}
    </span>
    <span className='ip-foot__issuer'>{issuer}</span>
  </footer>
)

const KeyValue: FC<{label: string; children: ReactNode; wide?: boolean}> = ({
  label,
  children,
  wide,
}) => (
  <div className={clsx('ip-kv__item', {'ip-kv__item--wide': wide})}>
    <span className='ip-kv__label'>{label}</span>
    <span className='ip-kv__value'>{children}</span>
  </div>
)

const PartyBox: FC<{title: string; party: InvoicePrintParty | null; empty: string}> = ({
  title,
  party,
  empty,
}) => (
  <section className='ip-party'>
    <h2 className='ip-party__title'>{title}</h2>
    {party ? (
      <div className='ip-kv'>
        {showsPartyName(party) && (
          <KeyValue label='نام' wide>
            <strong>{party.name ? persianText(party.name) : '—'}</strong>
          </KeyValue>
        )}
        {party.personTypeTitle && <KeyValue label='نوع شخص'>{party.personTypeTitle}</KeyValue>}
        {party.identifiers.map((identifier, index) => (
          <KeyValue key={`${identifier.key}-${index}`} label={identifier.title}>
            <FieldValue field={identifier} />
          </KeyValue>
        ))}
        {party.postalCode && (
          <KeyValue label='کد پستی'>
            <Code value={party.postalCode} />
          </KeyValue>
        )}
        {party.phone && (
          <KeyValue label='تلفن'>
            <Code value={party.phone} />
          </KeyValue>
        )}
        {party.address && (
          <KeyValue label='نشانی' wide>
            {persianText(party.address)}
          </KeyValue>
        )}
        {party.note && (
          <KeyValue label='توضیح' wide>
            {persianText(party.note)}
          </KeyValue>
        )}
      </div>
    ) : (
      <p className='ip-party__empty'>{empty}</p>
    )}
  </section>
)

export const PartiesBlock: FC<{invoice: InvoicePrintModel}> = ({invoice}) => (
  <div className='ip-parties'>
    <PartyBox title='مشخصات فروشنده' party={invoice.seller} empty='مشخصات فروشنده ثبت نشده است.' />
    <PartyBox
      title='مشخصات خریدار'
      party={invoice.buyer}
      empty={
        invoice.invoiceType === 1
          ? 'مشخصات خریدار در این صورتحساب ثبت نشده است.'
          : 'در صورتحساب نوع ۲ ثبت مشخصات خریدار الزامی نیست و ثبت نشده است.'
      }
    />
  </div>
)

export const hasPatternInfo = (invoice: InvoicePrintModel) =>
  invoice.patternFields.length > 0 ||
  invoice.shippedGoods.length > 0 ||
  invoice.notes.length > 0 ||
  invoice.article9 !== null

export const PatternInfoBlock: FC<{invoice: InvoicePrintModel}> = ({invoice}) => (
  <section className='ip-pattern'>
    <h2 className='ip-section-title'>اطلاعات الگوی {invoice.patternTitle}</h2>
    <div className='ip-kv ip-kv--grid'>
      {invoice.article9 && (
        <>
          <KeyValue label='قاعده ارسال صورتحساب'>
            ماده ۹ قانون پایانه‌های فروشگاهی و سامانه مؤدیان
          </KeyValue>
          {invoice.article9.registeredAt && (
            <KeyValue label='تاریخ و زمان ثبت صورتحساب'>
              <bdi className='ip-date'>{invoice.article9.registeredAt}</bdi>
            </KeyValue>
          )}
        </>
      )}
      {invoice.patternFields.map((field, index) => (
        <KeyValue key={`${field.key}-${index}`} label={field.title}>
          <FieldValue field={field} />
        </KeyValue>
      ))}
      {invoice.notes.map((note) => (
        <KeyValue key={note.key} label={note.title}>
          {persianText(note.value)}
        </KeyValue>
      ))}
    </div>
    {invoice.shippedGoods.length > 0 && (
      <div className='ip-shipped'>
        <span className='ip-kv__label'>کالاهای حمل‌شده</span>
        {invoice.shippedGoods.map((good, index) => (
          <span key={index} className='ip-shipped__item'>
            <Code value={good.stuffId} /> {good.title ? persianText(good.title) : ''}
          </span>
        ))}
      </div>
    )}
  </section>
)

const cellClass = (column: PrintColumn) =>
  clsx('ip-table__cell', `ip-table__cell--${column.kind}`, `ip-col-${column.key}`)

/**
 * The rows from..to of the invoice under the repeated column heads. With totals, the foot adds
 * the sheet's sum and the running sum of every summable column («جمع این صفحه» and «جمع کل»).
 * The measuring copy also lays out probeTotals in collapsed rows: they take no height but widen
 * the columns to hold every total the sheets print.
 */
export const ItemsTable: FC<{
  invoice: InvoicePrintModel
  columns: PrintColumn[]
  widths?: number[]
  from: number
  to: number
  totals?: RowsTotals
  footLabels?: {page: string; running: string}
  continued?: boolean
  probeTotals?: Record<string, string | null>[]
}> = ({invoice, columns, widths, from, to, totals, footLabels, continued, probeTotals}) => {
  const labelSpan = Math.max(
    1,
    columns.findIndex((column) => column.sum)
  )
  // Shares of the table, not pixels: the frozen columns always add up to the sheet's width.
  const widthsTotal = widths ? widths.reduce((sum, width) => sum + width, 0) : 0
  return (
    <div className='ip-items' data-measure='items-table'>
      <div className='ip-items__caption' data-measure='items-caption'>
        <span className='ip-items__caption-title'>
          {continued ? 'اقلام صورتحساب (ادامه)' : 'اقلام صورتحساب'}
        </span>
        <span className='ip-items__caption-note'>مبالغ به ریال</span>
      </div>
      <table className={clsx('ip-table', {'ip-table--fixed': !!widths})}>
        {widths && (
          <colgroup>
            {widths.map((width, index) => (
              <col key={index} style={{width: `${((width / widthsTotal) * 100).toFixed(4)}%`}} />
            ))}
          </colgroup>
        )}
        <thead data-measure='items-head'>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={cellClass(column)} scope='col'>
                <span className='ip-table__head'>{column.title}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {invoice.items.slice(from, to).map((item) => (
            <tr key={item.row} data-measure='items-row'>
              {columns.map((column) => {
                const secondary = column.secondary?.(item)
                const suffix = column.suffix?.(item)
                const raw = column.value(item)
                return (
                  <td key={column.key} className={cellClass(column)}>
                    {column.kind === 'code' ? (
                      <Code value={raw} />
                    ) : column.kind === 'amount' ||
                      column.kind === 'number' ||
                      column.kind === 'rate' ? (
                      <bdi dir='ltr' className='ip-num'>
                        {formatCell(column, raw)}
                      </bdi>
                    ) : column.kind === 'text' ? (
                      <span className='ip-table__text'>{raw}</span>
                    ) : (
                      raw
                    )}
                    {suffix && <span className='ip-table__suffix'>{suffix}</span>}
                    {secondary && <span className='ip-table__secondary'>{secondary}</span>}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
        {probeTotals && probeTotals.length > 0 && (
          <tbody className='ip-table__probe' aria-hidden='true'>
            {probeTotals.map((probe, index) => (
              <tr key={index} className='ip-table__total'>
                <th colSpan={labelSpan} />
                {columns.slice(labelSpan).map((column) => (
                  <td key={column.key} className={cellClass(column)}>
                    {column.sum && (
                      <bdi dir='ltr' className='ip-num'>
                        {formatNumber(probe[column.key])}
                      </bdi>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        )}
        {totals && footLabels && (
          <tfoot data-measure='items-foot'>
            {(['page', 'running'] as const).map((kind) => (
              <tr key={kind} className={clsx('ip-table__total', `ip-table__total--${kind}`)}>
                <th colSpan={labelSpan} scope='row' className='ip-table__total-label'>
                  {kind === 'page' ? footLabels.page : footLabels.running}
                </th>
                {columns.slice(labelSpan).map((column) => (
                  <td key={column.key} className={cellClass(column)}>
                    {column.sum && (
                      <bdi dir='ltr' className='ip-num'>
                        {formatNumber(totals[kind][column.key])}
                      </bdi>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tfoot>
        )}
      </table>
    </div>
  )
}

export const TotalsBlock: FC<{invoice: InvoicePrintModel}> = ({invoice}) => {
  const rows = totalRows(invoice)
  const settlement = invoice.settlement
  return (
    <section className='ip-totals'>
      <div className='ip-totals__side'>
        {invoice.totals.totalInWords && (
          <div className='ip-totals__words'>
            <span className='ip-kv__label'>مبلغ کل به حروف</span>
            <strong>{invoice.totals.totalInWords}</strong>
          </div>
        )}
        {settlement && (
          <div className='ip-totals__settlement'>
            <span className='ip-kv__label'>روش تسویه</span>
            <strong>{settlement.title}</strong>
            {settlement.cashAmount && (
              <span className='ip-totals__split'>
                مبلغ پرداختی نقدی <Num value={settlement.cashAmount} /> ریال
              </span>
            )}
            {settlement.creditAmount && (
              <span className='ip-totals__split'>
                مبلغ نسیه <Num value={settlement.creditAmount} /> ریال
              </span>
            )}
          </div>
        )}
        {invoice.invoiceDiscounts.length > 0 && (
          <div className='ip-totals__discounts'>
            <span className='ip-kv__label'>
              تخفیف‌های کل صورتحساب (در ستون تخفیف ردیف‌ها لحاظ شده)
            </span>
            {invoice.invoiceDiscounts.map((discount, index) => (
              <span key={index} className='ip-totals__split'>
                {discount.title ? persianText(discount.title) : 'تخفیف'}{' '}
                <Num value={discount.amount} /> ریال
              </span>
            ))}
          </div>
        )}
      </div>
      <table className='ip-totals__table'>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className={clsx({'ip-totals__strong': row.strong})}>
              <th scope='row'>{row.title}</th>
              <td>
                <bdi dir='ltr' className='ip-num'>
                  {row.value}
                </bdi>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

export const PaymentsTable: FC<{
  invoice: InvoicePrintModel
  from: number
  to: number
  continued?: boolean
}> = ({invoice, from, to, continued}) => (
  <section className='ip-payments'>
    <h2 className='ip-section-title' data-measure='payments-title'>
      {continued ? 'پرداخت‌ها (ادامه)' : 'پرداخت‌ها'}
    </h2>
    <table className='ip-table ip-table--payments'>
      <thead data-measure='payments-head'>
        <tr>
          <th scope='col'>ردیف</th>
          <th scope='col'>تاریخ و زمان پرداخت</th>
          <th scope='col'>روش پرداخت</th>
          <th scope='col'>شماره پیگیری/شماره مرجع</th>
          <th scope='col'>شماره کارت پرداخت‌کننده</th>
          <th scope='col'>مبلغ پرداختی (ریال)</th>
        </tr>
      </thead>
      <tbody>
        {invoice.payments.slice(from, to).map((payment, index) => (
          <tr key={from + index} data-measure='payments-row'>
            <td>{from + index + 1}</td>
            <td>
              <bdi className='ip-date'>{payment.paidAt}</bdi>
            </td>
            <td>{payment.methodTitle}</td>
            <td>
              <Code value={payment.traceNumber} />
            </td>
            <td>
              <Code value={payment.cardNumber} />
            </td>
            <td className='ip-table__cell--amount'>
              <Num value={payment.amount} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {to === invoice.payments.length && invoice.paymentsTotal && (
      <div className='ip-payments__summary' data-measure='payments-summary'>
        <span>
          جمع پرداخت‌ها <Num value={invoice.paymentsTotal} /> ریال
        </span>
        {invoice.remaining && (
          <span>
            مانده صورتحساب <Num value={invoice.remaining} /> ریال
          </span>
        )}
      </div>
    )}
  </section>
)

export const NotesBlock: FC<{lines: string[]; from: number; to: number; continued?: boolean}> = ({
  lines,
  from,
  to,
  continued,
}) => (
  <section className='ip-notes'>
    <h2 className='ip-section-title' data-measure='notes-title'>
      {continued ? 'توضیحات (ادامه)' : 'توضیحات'}
    </h2>
    <div className='ip-notes__body'>
      {lines.slice(from, to).map((line, index) => (
        <p
          key={from + index}
          className={clsx('ip-notes__line', {'ip-notes__line--gap': !line})}
          data-measure='notes-line'
        >
          {line || ' '}
        </p>
      ))}
    </div>
  </section>
)

export const NoticesBlock: FC<{invoice: InvoicePrintModel}> = ({invoice}) => (
  <section className='ip-notices'>
    {notices(invoice).map((notice, index) => (
      <p key={index}>{notice}</p>
    ))}
  </section>
)

/**
 * The seller's stamp prints at the template's height; a small picture is enlarged to it. Compact,
 * when that keeps the signatures off a sheet of their own: the stamp shrinks to a size that still
 * reads on paper (LEGIBLE_SIGNATURE_MM), never smaller — boxes without a stamp just get lower.
 */
export const SignaturesBlock: FC<{
  invoice: InvoicePrintModel
  showSignature: boolean
  layout: InvoicePrintLayoutSettings
  compact?: boolean
}> = ({invoice, showSignature, layout, compact}) => {
  const stamped = showSignature && !!invoice.signatureUrl
  const stampMm = compact
    ? Math.min(layout.signatureHeightMm, LEGIBLE_SIGNATURE_MM)
    : layout.signatureHeightMm
  return (
    <section
      className={clsx('ip-signatures', {
        'ip-signatures--compact': compact && !stamped,
        'ip-signatures--stamped': stamped,
        'ip-signatures--seller-only': !layout.showBuyerSignatureBox,
      })}
      style={stamped ? ({'--ip-sign-mm': `${stampMm}mm`} as CSSProperties) : undefined}
    >
      <div className='ip-signatures__box'>
        <span className='ip-signatures__title'>مهر و امضای فروشنده</span>
        {stamped && <img className='ip-signatures__image' src={invoice.signatureUrl!} alt='' />}
      </div>
      {layout.showBuyerSignatureBox && (
        <div className='ip-signatures__box'>
          <span className='ip-signatures__title'>مهر و امضای خریدار</span>
        </div>
      )}
    </section>
  )
}

export const Watermark: FC<{text: string | null}> = ({text}) =>
  text ? (
    <div
      className={clsx('ip-watermark', {'ip-watermark--info': watermarkTone(text) === 'info'})}
      aria-hidden='true'
    >
      <span>{text}</span>
    </div>
  ) : null

/** Every part of a sheet's body sits in one of these; its bottom padding is the gap to the next. */
export const BlockFrame: FC<{measureKey?: string; children: ReactNode}> = ({
  measureKey,
  children,
}) => (
  <div className='ip-block' data-measure-block={measureKey}>
    {children}
  </div>
)
